// useAuthencation.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changePassword, login, LoginSuccessResponse, me, updateProfile, register, sendOTP, verifyOTP, resetPassword, deleteAccount, sendVerificationEmail, SendVerificationEmailRequest, authGoogle, authApple } from '../api/authencation';
import { setTokens } from '../api/axios';
import { toast } from 'react-toastify';
import { create } from 'zustand';

interface AuthStore {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
}

const useAuthStore = create<AuthStore>((set) => ({
  isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
}));

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterCredentials {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  otpCode: string;
}

export const useAuth = () => {
  return useQuery({
    queryKey: ['auth'],
    queryFn: () => {
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      return Promise.resolve(isAuthenticated);
    },
  });
};

export const useLogin = () => {
    const queryClient = useQueryClient();
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  return useMutation<LoginSuccessResponse, Error, LoginCredentials>({
    mutationFn: ({ email, password }: LoginCredentials) => login(email, password),
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      localStorage.setItem('isAuthenticated', 'true');
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      return true;
    },
    onError: (error) => {
      toast.error(error.message);
      throw error;
    },
  });
};

export const useIsAuthenticated = () => {
  return useAuthStore((state) => state.isAuthenticated);
}; 

export const useMe = () => {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => me(),
    enabled: useIsAuthenticated(),
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useRegister = () => {
  return useMutation<void, Error, RegisterCredentials>({
    mutationFn: (data: RegisterCredentials) => register(data),
    onSuccess: () => {
      toast.success('Registration successful! Please login.');
      return true;
    },
    onError: (error) => {
      toast.error(error.message);
      throw error;
    },
  });
};

export const useSendOTP = () => {
  return useMutation({
    mutationFn: sendOTP,
    onSuccess: () => {
      toast.success('OTP sent successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: verifyOTP,
    onSuccess: () => {
      toast.success('OTP verified successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      toast.success('Password reset successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('isAuthenticated');

      window.location.href = '/main-login';
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useSendVerificationEmail = () => {
  return useMutation<string, Error, SendVerificationEmailRequest>({
    mutationFn: (data: SendVerificationEmailRequest) => sendVerificationEmail(data),
    onSuccess: (data) => {
      toast.success(data);
      return data;
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useAuthGoogle = () => {
  const queryClient = useQueryClient();
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  return useMutation<LoginSuccessResponse, Error, string>({
    mutationFn: (idToken: string) => authGoogle(idToken),
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      localStorage.setItem('isAuthenticated', 'true');
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      return true;
    },
    onError: (error) => {
      toast.error(error.message);
      throw error;
    },
  });
};

export const useAuthApple = () => {
  const queryClient = useQueryClient();
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  return useMutation<LoginSuccessResponse, Error, { idToken: string; firstName?: string; lastName?: string }>({
    mutationFn: ({ idToken, firstName, lastName }) => authApple(idToken, firstName, lastName),
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      localStorage.setItem('isAuthenticated', 'true');
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      return true;
    },
    onError: (error) => {
      toast.error(error.message);
      throw error;
    },
  });
};
